// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — EntityPicker: ONE config-driven picker usable by all dialogs.
// Server-side(-shaped) full-text search over name + attributes (AND-token),
// filter pills, list<->table toggle, virtualization, match-highlighting,
// multi-select, hit counter and an inline QuickCreate slot. Zero host coupling.
//
// Composed from small focused sub-components (FilterPopoverTrigger,
// ActiveFilterChips, SelectedChips, PickerResults + row renderers) so each
// unit stays simple/testable. Filter UI mirrors the app-standard
// `ListFilterBar` pattern (TES-2164): a filter-popover trigger with an
// active-count badge next to the search input, plus a persistent
// active-filters chip row underneath.

import * as React from "react";
import { useWindowedRows } from "./useWindowedRows";
import { Check, Filter, LayoutGrid, List, Plus, Search, X } from "lucide-react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { activateOnEnterSpace, cn } from "../lib/utils";
import { QuickCreate } from "./QuickCreate";
import { deriveFilterOptions, highlightMatches, tokenize } from "./search";
import { useEntitySearch } from "./useEntitySearch";
import type { UseEntitySearchState } from "./useEntitySearch";
import type {
  ColumnDef,
  EntityRecord,
  EntityTypeConfig,
  FilterPillDef,
  SearchProvider,
} from "./types";

const LIST_ROW_HEIGHT = 48;
const TABLE_ROW_HEIGHT = 40;

/** User-facing copy for the EntityPicker. German defaults; override per app/locale. */
export interface EntityPickerLabels {
  searchPlaceholder: (plural: string) => string;
  searchAria: (plural: string) => string;
  listView: string;
  tableView: string;
  searching: string;
  count: (n: number, singular: string, plural: string) => string;
  createNamed: (text: string) => string;
  noResults: (plural: string, text: string) => string;
  /** Label of the filter-popover trigger button (e.g. "Filter"). */
  filter: string;
  clearFilters: string;
  removeItem: (name: string) => string;
  /** Accessible label for a chip's remove button in the active-filters row. */
  removeFilter: (label: string) => string;
  selectItem: (name: string) => string;
  confirm: (n: number) => string;
}

const DEFAULT_LABELS: EntityPickerLabels = {
  searchPlaceholder: (plural) => `${plural} suchen…`,
  searchAria: (plural) => `${plural} suchen`,
  listView: "Listenansicht",
  tableView: "Tabellenansicht",
  searching: "Suche…",
  count: (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`,
  createNamed: (text) => `„${text}" anlegen`,
  noResults: (plural, text) => `Keine ${plural} gefunden${text ? ` für „${text}"` : ""}.`,
  filter: "Filter",
  clearFilters: "Filter zurücksetzen",
  removeItem: (name) => `${name} entfernen`,
  removeFilter: (label) => `Filter „${label}" entfernen`,
  selectItem: (name) => `${name} auswählen`,
  confirm: (n) => (n > 0 ? `${n} übernehmen` : "Übernehmen"),
};

export interface EntityPickerProps {
  config: EntityTypeConfig;
  provider: SearchProvider;
  /** Controlled selection. */
  value?: EntityRecord[];
  /** Uncontrolled initial selection. */
  defaultValue?: EntityRecord[];
  onChange?: (selected: EntityRecord[]) => void;
  /** Confirm in a modal/sheet context (single-select fires on pick). */
  onConfirm?: (selected: EntityRecord[]) => void;
  /** Override config.multiSelect. */
  multiple?: boolean;
  autoFocus?: boolean;
  className?: string;
  /** Override the user-facing copy (defaults are German). Pass localized strings in a de/en/ja app. */
  labels?: Partial<EntityPickerLabels>;
}

function Highlighted({ text, tokens }: { text: string; tokens: string[] }): React.ReactElement {
  const segments = highlightMatches(text, tokens);
  return (
    <>
      {segments.map((seg, i) =>
        seg.match ? (
          <mark
            key={i}
            className="rounded-sm bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-500/40"
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </>
  );
}

type PillGroup = FilterPillDef & { options: { value: string; label: string }[] };

/**
 * Filter-popover trigger — mirrors the app-standard `ListFilterBar` pattern
 * (`src/components/shared/ListFilterBar.tsx`, FAM1): a button with a
 * Filter icon + label + an active-count badge, opening a `Popover` that
 * holds the actual pill-toggle buttons (previously always-visible, now
 * tucked behind the trigger so the picker body stays compact).
 * Renders nothing when the config has no filter pills at all — same as the
 * previous always-visible `FilterPills` in that situation.
 */
function FilterPopoverTrigger({
  groups,
  filters,
  onToggle,
  activeCount,
  filterLabel,
  open,
  onOpenChange,
}: {
  groups: PillGroup[];
  filters: Record<string, string[]>;
  onToggle: (key: string, value: string) => void;
  activeCount: number;
  filterLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement | null {
  if (groups.length === 0) return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 gap-2"
          data-testid="filter-pills-trigger"
        >
          <Filter className="size-4" />
          {filterLabel}
          {activeCount > 0 ? (
            <Badge
              variant="secondary"
              className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full p-0 text-xs"
            >
              {activeCount}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // TES-2164: verified empirically (real Chromium via the /dev/ui-kit
        // harness, both desktop and a 390px mobile viewport) that EntityPicker
        // is very often mounted inside a LayeredModalHost layer (modal /
        // bottom-sheet) — see AssignedGroupField -> GroupPicker. That layer's
        // own panel is a `document.body` portal with an explicit
        // `zIndex: 60 + layer.id` (LayeredModalHost.tsx). Our Popover content
        // is ALSO a `document.body` portal (Radix Popover.Portal) but the base
        // `PopoverContent` only carries the shadcn-default `z-50` — lower than
        // ANY open layer, so the popover silently rendered BEHIND the layer's
        // own header row (pointer events swallowed by that row, not a11y-
        // visible at all) instead of on top of it. `z-[100]` reuses this
        // repo's existing "always on top" convention (see PdfImageLightbox.tsx,
        // AppLayout.tsx's skip-link) and comfortably outranks any realistic
        // LayeredModalHost nesting depth.
        className="z-[100] w-72"
        align="end"
        data-testid="filter-pills"
        // TES-2164 (see TES-2105 in LayeredModalHost.tsx): that same nested-
        // layer-inside-an-ambient-Radix-Dialog scenario also sets
        // `document.body.style.pointerEvents = "none"` while the Dialog is
        // open, re-enabling "auto" ONLY on its own registered layer node. This
        // Popover's Content is a separate `document.body` portal, not a
        // descendant of that node, so without an explicit local override real
        // pointer clicks would silently fail to reach the pill buttons below
        // — exactly the class of bug TES-2105 fixed for row clicks. Setting
        // the inline style locally (like LayeredModalHost's
        // DismissableLayerBranch does) reproduces that fix here too. `zIndex`
        // is ALSO set inline (redundant with the `z-[100]` class in real
        // browsers, inline always wins) so the fix is assertable in jsdom
        // unit tests too, same as the pointerEvents override above — jsdom
        // never loads compiled Tailwind CSS, so arbitrary-value classes
        // alone aren't observable via getComputedStyle in tests.
        style={{ pointerEvents: "auto", zIndex: 100 }}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {groups.map((pill) =>
            pill.options.map((opt) => {
              const active = (filters[pill.key] ?? []).includes(opt.value);
              return (
                <button
                  key={`${pill.key}:${opt.value}`}
                  type="button"
                  data-testid={`pill-${pill.key}-${opt.value}`}
                  aria-pressed={active}
                  onClick={() => onToggle(pill.key, opt.value)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background hover:bg-accent",
                  )}
                >
                  {opt.label}
                  {active ? <Check className="size-3" /> : null}
                </button>
              );
            }),
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Persistent active-filters chip row — mirrors `ListFilterBar`'s row 2
 * (label-less here since the picker body is compact; the badge on the
 * trigger already communicates the count). One chip per active filter
 * VALUE (not per key — a key with two active values renders two chips), a
 * per-chip X to remove just that value, and a "Clear all" button while at
 * least one filter is active. Rendered only when something is active so it
 * never reserves empty vertical space inside the already height-constrained
 * picker body (`h-[70vh]` dialogs / mobile bottom sheets).
 */
function ActiveFilterChips({
  groups,
  filters,
  onToggle,
  onClear,
  activeCount,
  clearLabel,
  removeLabel,
}: {
  groups: PillGroup[];
  filters: Record<string, string[]>;
  onToggle: (key: string, value: string) => void;
  onClear: () => void;
  activeCount: number;
  clearLabel: string;
  removeLabel: (label: string) => string;
}): React.ReactElement | null {
  if (activeCount === 0) return null;
  return (
    <div
      className="flex flex-nowrap items-center gap-1.5 overflow-x-auto [&>*]:shrink-0 [&>*]:whitespace-nowrap"
      data-testid="active-filter-chips"
    >
      {groups.flatMap((pill) =>
        (filters[pill.key] ?? []).map((value) => {
          const label = pill.options.find((o) => o.value === value)?.label ?? value;
          return (
            <Badge
              key={`${pill.key}:${value}`}
              variant="secondary"
              className="gap-1"
              data-testid={`active-filter-chip-${pill.key}-${value}`}
            >
              {label}
              <button
                type="button"
                aria-label={removeLabel(label)}
                data-testid={`remove-filter-${pill.key}-${value}`}
                onClick={() => onToggle(pill.key, value)}
                className="ml-0.5 rounded-full hover:text-destructive"
              >
                <X className="size-3" />
              </button>
            </Badge>
          );
        }),
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClear}
        data-testid="clear-filters"
        className="h-6 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
      >
        <X className="size-3" /> {clearLabel}
      </Button>
    </div>
  );
}

function SelectedChips({
  selected,
  onRemove,
  removeLabel,
}: {
  selected: EntityRecord[];
  onRemove: (e: EntityRecord) => void;
  removeLabel: (name: string) => string;
}): React.ReactElement | null {
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="selected-chips">
      {selected.map((s) => (
        <Badge key={s.id} variant="secondary" className="gap-1">
          {s.name}
          <button
            type="button"
            aria-label={removeLabel(s.name)}
            onClick={() => onRemove(s)}
            className="ml-0.5 rounded-full hover:text-destructive"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

function cellValue(entity: EntityRecord, key: string): string {
  if (key === "name") return entity.name;
  if (key === "subtitle") return entity.subtitle ?? "";
  return entity.attributes?.[key] ?? "";
}

function ListRow({
  entity,
  selected,
  isMulti,
  tokens,
  style,
  onPick,
  selectLabel,
}: {
  entity: EntityRecord;
  selected: boolean;
  isMulti: boolean;
  tokens: string[];
  style: React.CSSProperties;
  onPick: (e: EntityRecord) => void;
  selectLabel: (name: string) => string;
}): React.ReactElement {
  return (
    <div
      data-testid={`row-${entity.id}`}
      role="option"
      aria-selected={selected}
      tabIndex={entity.disabled ? -1 : 0}
      onClick={() => onPick(entity)}
      onKeyDown={activateOnEnterSpace(() => onPick(entity))}
      className={cn(
        "absolute left-0 top-0 flex w-full cursor-pointer items-center gap-3 border-b border-border/60 px-3 hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected && "bg-accent",
        entity.disabled && "cursor-not-allowed opacity-50",
      )}
      style={style}
    >
      {isMulti ? <Checkbox checked={selected} aria-label={selectLabel(entity.name)} /> : null}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">
          <Highlighted text={entity.name} tokens={tokens} />
        </div>
        {entity.subtitle ? (
          <div className="truncate text-xs text-muted-foreground">
            <Highlighted text={String(entity.subtitle)} tokens={tokens} />
          </div>
        ) : null}
      </div>
      {!isMulti && selected ? <Check className="size-4 text-primary" /> : null}
    </div>
  );
}

function TableRow({
  entity,
  selected,
  isMulti,
  columns,
  tokens,
  style,
  onPick,
  selectLabel,
  gridTemplate,
}: {
  entity: EntityRecord;
  selected: boolean;
  isMulti: boolean;
  columns: ColumnDef[];
  tokens: string[];
  style: React.CSSProperties;
  onPick: (e: EntityRecord) => void;
  selectLabel: (name: string) => string;
  gridTemplate: string;
}): React.ReactElement {
  return (
    <div
      data-testid={`row-${entity.id}`}
      role="option"
      aria-selected={selected}
      tabIndex={entity.disabled ? -1 : 0}
      onClick={() => onPick(entity)}
      onKeyDown={activateOnEnterSpace(() => onPick(entity))}
      className={cn(
        "absolute left-0 top-0 grid w-full cursor-pointer items-center border-b border-border/60 hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected && "bg-accent",
        entity.disabled && "cursor-not-allowed opacity-50",
      )}
      style={{ ...style, gridTemplateColumns: gridTemplate }}
    >
      {isMulti ? (
        <div className="px-3">
          <Checkbox checked={selected} aria-label={selectLabel(entity.name)} />
        </div>
      ) : null}
      {columns.map((c) => (
        <div key={c.key} className="truncate px-3">
          <Highlighted text={String(cellValue(entity, c.key))} tokens={tokens} />
        </div>
      ))}
    </div>
  );
}

function PickerResults({
  search,
  config,
  view,
  tokens,
  isMulti,
  selectedIds,
  onPick,
  canCreate,
  onStartCreate,
  labels,
}: {
  search: UseEntitySearchState;
  config: EntityTypeConfig;
  view: "list" | "table";
  tokens: string[];
  isMulti: boolean;
  selectedIds: Set<string>;
  onPick: (e: EntityRecord) => void;
  canCreate: boolean;
  onStartCreate: () => void;
  labels: EntityPickerLabels;
}): React.ReactElement {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const rowHeight = view === "list" ? LIST_ROW_HEIGHT : TABLE_ROW_HEIGHT;
  // Shared grid template for the table view (header + every row) so columns align.
  const tableGrid =
    (isMulti ? "2.5rem " : "") +
    config.columns.map((c) => (c.width ? `${c.width}px` : "minmax(0, 1fr)")).join(" ");
  const getScrollElement = React.useCallback(() => scrollRef.current, []);
  const { rows: virtualRows, totalSize } = useWindowedRows({
    count: search.results.length,
    rowHeight,
    getScrollElement,
  });

  // Infinite scroll: append the next page as the last rendered row nears the end.
  const lastIndex = virtualRows.length > 0 ? virtualRows[virtualRows.length - 1].index : -1;
  React.useEffect(() => {
    if (search.hasMore && !search.loadingMore && lastIndex >= search.results.length - 8) {
      search.loadMore();
    }
  }, [lastIndex, search]);

  if (search.error) {
    return (
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
        data-testid="entity-picker-scroll"
      >
        <p className="p-4 text-sm text-destructive" role="alert">
          {search.error.message}
        </p>
      </div>
    );
  }

  if (search.results.length === 0 && !search.loading) {
    return (
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
        data-testid="entity-picker-scroll"
      >
        <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-muted-foreground">
          <span data-testid="no-results">{labels.noResults(config.labelPlural, search.text)}</span>
          {canCreate && config.quickCreate ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="quick-create-empty"
              onClick={onStartCreate}
            >
              <Plus className="size-4" /> {config.quickCreate.label}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
      data-testid="entity-picker-scroll"
    >
      {view === "table" ? (
        <div className="text-sm" data-testid="entity-table">
          {/* CSS grid (shared template) so header + virtualized rows align, even
              when columns have no explicit width (the <table>/display:block split
              would otherwise desync header and body widths). */}
          {/* Purely-visual column labels for the listbox below — NOT a grid/table,
              so no row/columnheader roles (they'd be orphaned without a table ancestor). */}
          <div
            aria-hidden="true"
            className="sticky top-0 z-10 grid items-center bg-muted/80 backdrop-blur"
            style={{ gridTemplateColumns: tableGrid }}
          >
            {isMulti ? <div className="px-3 py-2" /> : null}
            {config.columns.map((c) => (
              <div
                key={c.key}
                className="truncate px-3 py-2 text-left font-medium text-muted-foreground"
              >
                {c.label}
              </div>
            ))}
          </div>
          <div
            role="listbox"
            aria-multiselectable={isMulti}
            aria-label={config.labelPlural}
            style={{ height: totalSize, position: "relative" }}
          >
            {virtualRows.map((vr) => {
              const entity = search.results[vr.index];
              return (
                <TableRow
                  key={entity.id}
                  entity={entity}
                  selected={selectedIds.has(entity.id)}
                  isMulti={isMulti}
                  columns={config.columns}
                  tokens={tokens}
                  style={{ height: vr.size, transform: `translateY(${vr.start}px)` }}
                  onPick={onPick}
                  selectLabel={labels.selectItem}
                  gridTemplate={tableGrid}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <div
          role="listbox"
          aria-multiselectable={isMulti}
          aria-label={config.labelPlural}
          style={{ height: totalSize, position: "relative" }}
          data-testid="entity-list"
        >
          {virtualRows.map((vr) => {
            const entity = search.results[vr.index];
            return (
              <ListRow
                key={entity.id}
                entity={entity}
                selected={selectedIds.has(entity.id)}
                isMulti={isMulti}
                tokens={tokens}
                style={{ height: vr.size, transform: `translateY(${vr.start}px)` }}
                onPick={onPick}
                selectLabel={labels.selectItem}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PickerHeader({
  config,
  labels,
  text,
  onTextChange,
  view,
  onViewChange,
  inputRef,
  hitText,
  showCreateTrigger,
  createLabel,
  onStartCreate,
  filterTrigger,
}: {
  config: EntityTypeConfig;
  labels: EntityPickerLabels;
  text: string;
  onTextChange: (t: string) => void;
  view: "list" | "table";
  onViewChange: (v: "list" | "table") => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  hitText: string;
  showCreateTrigger: boolean;
  createLabel: string;
  onStartCreate: () => void;
  /** The filter-popover trigger button (null when the config has no filter pills). */
  filterTrigger?: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder={labels.searchPlaceholder(config.labelPlural)}
            className="pl-8"
            data-testid="entity-picker-search"
            aria-label={labels.searchAria(config.labelPlural)}
          />
        </div>
        {filterTrigger}
        <div className="flex items-center rounded-md border border-input">
          <Button
            type="button"
            variant={view === "list" ? "secondary" : "ghost"}
            size="icon"
            className="h-9 w-9 rounded-r-none"
            aria-pressed={view === "list"}
            aria-label={labels.listView}
            data-testid="view-toggle-list"
            onClick={() => onViewChange("list")}
          >
            <List />
          </Button>
          <Button
            type="button"
            variant={view === "table" ? "secondary" : "ghost"}
            size="icon"
            className="h-9 w-9 rounded-l-none"
            aria-pressed={view === "table"}
            aria-label={labels.tableView}
            data-testid="view-toggle-table"
            onClick={() => onViewChange("table")}
          >
            <LayoutGrid />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="hit-counter">{hitText}</span>
        {showCreateTrigger ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7"
            data-testid="quick-create-trigger"
            onClick={onStartCreate}
          >
            <Plus className="size-4" />
            {createLabel}
          </Button>
        ) : null}
      </div>
    </>
  );
}

export function EntityPicker({
  config,
  provider,
  value,
  defaultValue = [],
  onChange,
  onConfirm,
  multiple,
  autoFocus,
  className,
  labels,
}: EntityPickerProps): React.ReactElement {
  const L = React.useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const isMulti = multiple ?? config.multiSelect ?? false;
  const [view, setView] = React.useState<"list" | "table">(config.defaultView ?? "list");
  const [showQuickCreate, setShowQuickCreate] = React.useState(false);
  const [canCreate, setCanCreate] = React.useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = React.useState(false);

  // Selection (controlled or uncontrolled).
  const [internalSelected, setInternalSelected] = React.useState<EntityRecord[]>(defaultValue);
  const selected = value ?? internalSelected;
  const setSelected = React.useCallback(
    (next: EntityRecord[]) => {
      if (value === undefined) setInternalSelected(next);
      onChange?.(next);
    },
    [value, onChange],
  );

  const search = useEntitySearch({ config, provider });
  const tokens = React.useMemo(() => tokenize(search.text), [search.text]);

  // Resolve the create-right gate (may be async).
  React.useEffect(() => {
    let active = true;
    Promise.resolve(provider.canCreate?.(config) ?? false).then((ok) => {
      if (active) setCanCreate(!!ok);
    });
    return () => {
      active = false;
    };
  }, [provider, config]);

  const selectedIds = React.useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const handlePick = React.useCallback(
    (entity: EntityRecord) => {
      if (entity.disabled) return;
      if (isMulti) {
        const next = selectedIds.has(entity.id)
          ? selected.filter((s) => s.id !== entity.id)
          : [...selected, entity];
        setSelected(next);
        return;
      }
      setSelected([entity]);
      onConfirm?.([entity]);
    },
    [isMulti, selected, selectedIds, setSelected, onConfirm],
  );

  const handleCreated = React.useCallback(
    (record: EntityRecord) => {
      // Auto-select the freshly created record and surface it immediately.
      search.prepend(record);
      setShowQuickCreate(false);
      if (isMulti) {
        setSelected([...selected, record]);
        return;
      }
      setSelected([record]);
      onConfirm?.([record]);
    },
    [isMulti, selected, setSelected, onConfirm, search],
  );

  // Filter pills: fixed options or derived from the current result set.
  const pillGroups = React.useMemo<PillGroup[]>(
    () =>
      (config.filterPills ?? []).map((pill) => ({
        ...pill,
        options: pill.options ?? deriveFilterOptions(search.results, pill.key),
      })),
    [config.filterPills, search.results],
  );
  const activeFilterCount = Object.values(search.filters).reduce((n, v) => n + v.length, 0);

  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const showQuickCreateTrigger = canCreate && !!config.quickCreate && !showQuickCreate;

  return (
    <div
      className={cn("flex h-full min-h-0 flex-col gap-3", className)}
      data-testid="entity-picker"
    >
      <PickerHeader
        config={config}
        labels={L}
        text={search.text}
        onTextChange={(t) => {
          setShowQuickCreate(false);
          search.setText(t);
        }}
        view={view}
        onViewChange={setView}
        inputRef={inputRef}
        hitText={
          search.loading
            ? L.searching
            : L.count(search.total, config.labelSingular, config.labelPlural)
        }
        showCreateTrigger={showQuickCreateTrigger}
        createLabel={search.text ? L.createNamed(search.text) : (config.quickCreate?.label ?? "")}
        onStartCreate={() => setShowQuickCreate(true)}
        filterTrigger={
          <FilterPopoverTrigger
            groups={pillGroups}
            filters={search.filters}
            onToggle={search.toggleFilter}
            activeCount={activeFilterCount}
            filterLabel={L.filter}
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
          />
        }
      />

      <ActiveFilterChips
        groups={pillGroups}
        filters={search.filters}
        onToggle={search.toggleFilter}
        onClear={search.clearFilters}
        activeCount={activeFilterCount}
        clearLabel={L.clearFilters}
        removeLabel={L.removeFilter}
      />

      {isMulti ? (
        <SelectedChips selected={selected} onRemove={handlePick} removeLabel={L.removeItem} />
      ) : null}

      {showQuickCreate ? (
        <QuickCreate
          config={config}
          provider={provider}
          initialName={search.text}
          onCreated={handleCreated}
          onCancel={() => setShowQuickCreate(false)}
        />
      ) : (
        <PickerResults
          search={search}
          config={config}
          view={view}
          tokens={tokens}
          isMulti={isMulti}
          selectedIds={selectedIds}
          onPick={handlePick}
          canCreate={canCreate}
          onStartCreate={() => setShowQuickCreate(true)}
          labels={L}
        />
      )}

      {isMulti && onConfirm ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            data-testid="entity-picker-confirm"
            onClick={() => onConfirm(selected)}
          >
            {L.confirm(selected.length)}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
